<template>
    <v-row no-gutters class="fill-height">
        <v-col cols="12">
            <!-- Header with Gradient Background -->
            <v-sheet 
                color="primary" 
                class="width-100 pa-6 d-flex flex-column align-center justify-center rounded-lg mb-6 flex-shrink-0"
                style="background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken2) 100%); width: 100%;"
                elevation="4"
            >
                <v-icon x-large color="white" class="mb-4">mdi-file-document-edit</v-icon>
                <h1 id="tour-contract-templates-title" class="text-h4 font-weight-bold white--text mb-2 text-center">
                    Modelos de Contrato
                </h1>
                <div class="white--text text-subtitle-1 opacity-80 text-center" style="max-width: 600px;">
                    Gerencie os termos legais e condições para cada serviço. Configure variáveis dinâmicas para automação.
                </div>
            </v-sheet>

            <!-- Main Content Area -->
            <div class="">
                <v-card class="rounded-lg" elevation="2">
                    <v-card-title class="py-4 px-6">
                        <div class="d-flex align-center flex-grow-1">
                            <v-icon left color="primary" class="mr-2">mdi-briefcase-search</v-icon>
                            <span class="text-h6 font-weight-bold grey--text text--darken-3">Serviços Disponíveis</span>
                            
                            <v-spacer></v-spacer>
                            
                            <div class="d-flex align-center" style="width: 300px;">
                                <v-text-field
                                    id="tour-contract-templates-search"
                                    v-model="search"
                                    append-icon="mdi-magnify"
                                    label="Buscar serviço..."
                                    single-line
                                    hide-details
                                    dense
                                    outlined
                                    rounded
                                    class="search-input"
                                    color="primary"
                                    background-color="grey lighten-5"
                                ></v-text-field>
                            </div>
                        </div>
                    </v-card-title>
                    <v-divider></v-divider>
                    
                    <v-data-table
                        id="tour-contract-templates-table"
                        :headers="headers"
                        :items="servicesWithTemplateStatus"
                        :search="search"
                        :loading="loading"
                        class="elevation-0"
                        :footer-props="{
                            'items-per-page-options': [10, 20, 50, -1],
                            'items-per-page-text': 'Linhas por página:'
                        }"
                    >
                        <template v-slot:item.has_template="{ item }">
                            <v-chip
                                small
                                label
                                :color="item.has_template ? 'success lighten-4' : 'amber lighten-4'"
                                :text-color="item.has_template ? 'success darken-2' : 'amber darken-4'"
                                class="font-weight-medium"
                            >
                                <v-icon left x-small>
                                    {{ item.has_template ? 'mdi-check-circle' : 'mdi-alert' }}
                                </v-icon>
                                {{ item.has_template ? 'Configurado' : 'Pendente' }}
                            </v-chip>
                        </template>
                        
                        <template v-slot:item.actions="{ item }">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        small
                                        :color="item.has_template ? 'primary' : 'success'"
                                        @click="openTemplateEditor(item)"
                                        v-bind="attrs"
                                        v-on="on"
                                        class="mx-1"
                                    >
                                        <v-icon>
                                            {{ item.has_template ? 'mdi-pencil' : 'mdi-plus-circle-outline' }}
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ item.has_template ? 'Editar Modelo' : 'Criar Modelo' }}</span>
                            </v-tooltip>
                        </template>

                        <template v-slot:no-data>
                            <div class="pa-8 text-center grey--text text--darken-1">
                                <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-briefcase-off</v-icon>
                                <div>Nenhum serviço encontrado.</div>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </div>
        </v-col>


        <!-- Template Editor Dialog -->
        <v-dialog v-model="showEditorDialog" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
            <v-card>
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click="closeEditor">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>
                        {{ currentService ? `Modelo de Contrato - ${currentService.name}` : 'Modelo de Contrato' }}
                    </v-toolbar-title>
                    <v-spacer />
                    <v-toolbar-items>
                        <v-btn dark text @click="closeEditor">Fechar</v-btn>
                    </v-toolbar-items>
                </v-toolbar>

                <v-card-text class="pa-0">
                    <v-container fluid class="pa-6">
                        <v-row>
                            <!-- Editor Column -->
                            <v-col cols="12" md="6">
                                <div class="d-flex align-center mb-3">
                                    <v-icon color="primary" class="mr-2">mdi-file-edit</v-icon>
                                    <span class="text-h6 font-weight-bold">Editor</span>
                                </div>
                                
                                <!-- Help Alert -->
                                <v-alert type="info" text dense class="mb-4">
                                    <div class="text-caption">
                                        <strong>Dica:</strong> Use as variáveis para inserir dados dinâmicos no contrato.
                                        As variáveis serão substituídas automaticamente ao gerar contratos para clientes.
                                    </div>
                                </v-alert>

                                <tip-tap-editor v-model="templateContent" />
                            </v-col>

                            <!-- Preview Column -->
                            <v-col cols="12" md="6">
                                <div class="d-flex align-center mb-3">
                                    <v-icon color="primary" class="mr-2">mdi-file-eye</v-icon>
                                    <span class="text-h6 font-weight-bold">Pré-visualização</span>
                                </div>

                                <v-alert type="warning" text dense class="mb-4">
                                    <div class="text-caption">
                                        A pré-visualização mostra dados de exemplo. Os valores reais serão inseridos ao gerar o contrato.
                                    </div>
                                </v-alert>

                                <v-card outlined class="pa-4 preview-card">
                                    <div class="preview-content" v-html="previewHtml"></div>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <!-- Footer Actions -->
                <v-divider />
                <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn text @click="closeEditor">Cancelar</v-btn>
                    <v-btn color="primary" :loading="saving" @click="saveTemplate">
                        <v-icon left>mdi-content-save</v-icon>
                        Salvar Modelo
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Success Snackbar -->
        <v-snackbar v-model="showSuccessSnackbar" color="success" timeout="3000">
            <v-icon left>mdi-check-circle</v-icon>
            {{ successMessage }}
        </v-snackbar>

        <!-- Error Snackbar -->
        <v-snackbar v-model="showErrorSnackbar" color="error" timeout="5000">
            <v-icon left>mdi-alert-circle</v-icon>
            {{ errorMessage }}
        </v-snackbar>
    </v-row>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import TipTapEditor from './components/TipTapEditor.vue';
import dayjs from 'dayjs';
import axios from 'axios';

export default {
    name: 'ContractTemplatesPage',
    components: {
        TipTapEditor
    },
    data() {
        return {
            search: '',
            headers: [
                { text: 'Serviço', value: 'name', sortable: true },
                { text: 'Status', value: 'has_template', sortable: true, align: 'center' },
                { text: 'Ações', value: 'actions', sortable: false, align: 'center' }
            ],
            showEditorDialog: false,
            currentService: null,
            templateContent: '',
            saving: false,
            showSuccessSnackbar: false,
            successMessage: '',
            showErrorSnackbar: false,
            errorMessage: '',
            // Tour-specific data
            fakeServicesForTour: [],
            fakeTemplatesForTour: []
        };
    },
    computed: {
        ...mapState('services', {
            services_list: state => state.services_list
        }),
        ...mapState('contractTemplates', {
            contract_templates: state => state.contract_templates,
            loading: state => state.loading
        }),
        servicesWithTemplateStatus() {
            // Use fake data if available for tour
            const servicesToUse = this.fakeServicesForTour.length > 0 ? this.fakeServicesForTour : (this.services_list || []);
            const templatesToUse = this.fakeTemplatesForTour.length > 0 ? this.fakeTemplatesForTour : (this.contract_templates || []);
            
            return servicesToUse.map(service => {
                const hasTemplate = templatesToUse.some(t => t.service_id === service.id);
                return {
                    ...service,
                    has_template: hasTemplate
                };
            });
        },
        previewHtml() {
            let html = this.templateContent || '<p>Digite o conteúdo do contrato no editor...</p>';
            
            // Sample data for preview
            const sampleData = {
                CONTRACTOR: 'João da Silva',
                CPF: '123.456.789-00',
                RG: '12.345.678-9',
                PAYER_NAME: 'Maria da Silva',
                PAYER_CPF: '987.654.321-00',
                SERVICE_NAME: this.currentService ? this.currentService.name : 'Nome do Serviço',
                MINUTES: '50',
                WEEKDAY: 'Segunda-feira',
                HOUR: '14:00',
                FREQUENCY: 'Semanal',
                VALUE: 'R$ 150,00',
                FULL_VALUE: 'cento e cinquenta reais',
                PAYMENT_TYPE: 'Mensal',
                CANCEL_VALUE: 'R$ 20,00',
                DATE: dayjs().format('DD [de] MMMM [de] YYYY'),
                DOCTOR_NAME: 'Dr. Carlos Mendes',
                DOCTOR_EMAIL: 'carlos@exemplo.com'
            };

            // Replace all variables with sample data
            Object.keys(sampleData).forEach(key => {
                const regex = new RegExp(`\\[${key}\\]`, 'g');
                html = html.replace(regex, `<span class="variable-preview">${sampleData[key]}</span>`);
            });

            return html;
        }
    },
    mounted() {
        this.loadData();
        // Expose for tour
        window.$contractTemplatesPage = this;
    },
    beforeDestroy() {
        // Clean up global reference
        if (window.$contractTemplatesPage === this) {
            window.$contractTemplatesPage = null;
        }
        this.removeFakeTemplatesForTour();
    },
    methods: {
        ...mapActions('services', ['ActionDoGetServices']),
        ...mapActions('contractTemplates', [
            'ActionDoGetContractTemplates',
            'ActionDoGetContractTemplateByService',
            'ActionDoSaveContractTemplate'
        ]),
        async loadData() {
            try {
                await this.ActionDoGetServices();
                await this.ActionDoGetContractTemplates();
            } catch (error) {
                console.error('Error loading data:', error);
                this.showError('Erro ao carregar dados');
            }
        },
        async openTemplateEditor(service) {
            this.currentService = service;
            this.templateContent = '';
            
            try {
                const response = await this.ActionDoGetContractTemplateByService(service.id);
                if (response.data && response.data.template_content) {
                    this.templateContent = response.data.template_content;
                }
            } catch (error) {
                console.error('Error loading template:', error);
            }

            if (!this.templateContent) {
                try {
                    const response = await axios.get('/contract/contract.html');
                    this.templateContent = response.data;
                } catch (error) {
                    console.error("Erro ao carregar contrato padrão", error);
                }
            }
            
            this.showEditorDialog = true;
        },
        closeEditor() {
            this.showEditorDialog = false;
            this.currentService = null;
            this.templateContent = '';
        },
        async saveTemplate() {
            if (!this.currentService) return;
            
            this.saving = true;
            try {
                await this.ActionDoSaveContractTemplate({
                    service_id: this.currentService.id,
                    template_content: this.templateContent
                });
                
                this.showSuccess('Modelo de contrato salvo com sucesso!');
                await this.ActionDoGetContractTemplates();
                this.closeEditor();
            } catch (error) {
                console.error('Error saving template:', error);
                this.showError(error.response?.data?.message || 'Erro ao salvar modelo de contrato');
            } finally {
                this.saving = false;
            }
        },
        showSuccess(message) {
            this.successMessage = message;
            this.showSuccessSnackbar = true;
        },
        showError(message) {
            this.errorMessage = message;
            this.showErrorSnackbar = true;
        },
        // Tour helper methods
        addFakeTemplatesForTour() {
            this.fakeServicesForTour = [
                { id: 'tour-fake-1', name: 'Psicoterapia Individual' },
                { id: 'tour-fake-2', name: 'Avaliação Neuropsicológica' },
                { id: 'tour-fake-3', name: 'Supervisão Clínica' },
                { id: 'tour-fake-4', name: 'Workshop de Ansiedade' }
            ];
            
            // Only 1 and 3 are configured
            this.fakeTemplatesForTour = [
                { id: 'temp-1', service_id: 'tour-fake-1', template_content: '<p>Contrato de Psicoterapia...</p>' },
                { id: 'temp-3', service_id: 'tour-fake-3', template_content: '<p>Contrato de Supervisão...</p>' }
            ];
            
            console.log('[Tour] Fake templates added');
        },
        removeFakeTemplatesForTour() {
            this.fakeServicesForTour = [];
            this.fakeTemplatesForTour = [];
            console.log('[Tour] Fake templates removed');
        }
    }
};
</script>

<style scoped>
.preview-card {
    min-height: 500px;
    max-height: 700px;
    overflow-y: auto;
}

.preview-content {
    line-height: 1.6;
}

.preview-content >>> .variable-preview {
    background-color: #fff3cd;
    padding: 2px 4px;
    border-radius: 3px;
    font-weight: 500;
    color: #856404;
}

.preview-content >>> h1,
.preview-content >>> h2,
.preview-content >>> h3,
.preview-content >>> h4,
.preview-content >>> h5,
.preview-content >>> h6 {
    margin: 1em 0 0.5em 0;
    font-weight: bold;
}

.preview-content >>> p {
    margin: 0 0 1em 0;
}

.preview-content >>> img {
    max-width: 100%;
    height: auto;
}

.preview-content >>> ul,
.preview-content >>> ol {
    padding-left: 1.5em;
    margin: 0 0 1em 0;
}

.preview-content >>> a {
    color: #1976d2;
    text-decoration: underline;
}
</style>
